import React, { useState, useEffect, useRef } from 'react';
import validationAuth from '../../../validationAuth';
import {
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Paper,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    Divider,
    IconButton,
    Collapse,
    Chip,
    Slider,
    Tooltip
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    FitScreen as FitScreenIcon
} from '@mui/icons-material';
import OrgTree from 'react-org-tree';
import axios from 'axios';
import * as common from '../../../commons/common';

const OrgManagement = () => {
    const [showGeneralDepartmentSelect, setShowGeneralDepartmentSelect] = useState(false);
    const [showManagementDepartmentSelect, setShowManagementDepartmentSelect] = useState(false);
    const [showNewDepartmentInput, setShowNewDepartmentInput] = useState(false);
    const [selectedTeamDepartment, setSelectedTeamDepartment] = useState('');
    const [newDepartment, setNewDepartment] = useState('');
    const [departmentType, setDepartmentType] = useState('');
    const [selectedGeneralDepartment, setSelectedGeneralDepartment] = useState('');
    const [selectedManagementDepartment, setSelectedManagementDepartment] = useState('');
    const [organizationData, setOrganizationData] = useState(null);
    const [generalDepartments, setGeneralDepartments] = useState([]);
    const [managementDepartments, setManagementDepartments] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [editDepartmentId, setEditDepartmentId] = useState(null);
    const [editDepartmentName, setEditDepartmentName] = useState('');
    const [formExpanded, setFormExpanded] = useState(false);
    const [treeScale, setTreeScale] = useState(1);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const orgTreeRef = useRef(null);

    useEffect(() => {
        fetchOrganizationData();
    }, []);

    useEffect(() => {
        if (organizationData && orgTreeRef.current) {
            setTimeout(() => {
                const container = orgTreeRef.current;
                const containerWidth = container.offsetWidth;
                const containerHeight = container.offsetHeight;

                // 조직도의 예상 너비와 높이 계산
                const calculateTreeDimensions = (data) => {
                    if (!data || !data.children) return { width: 0, height: 0 };

                    const nodeWidth = isMobile ? 50 : 140;
                    const nodeHeight = isMobile ? 32 : 90;
                    const nodeGapX = isMobile ? 2 : 50;
                    const nodeGapY = isMobile ? 6 : 30;

                    const getMaxWidth = (node, level = 0) => {
                        if (!node.children || node.children.length === 0) {
                            return nodeWidth;
                        }
                        const childrenWidth = node.children.reduce((sum, child) => {
                            return sum + getMaxWidth(child, level + 1) + nodeGapX;
                        }, 0) - nodeGapX;
                        return Math.max(nodeWidth, childrenWidth);
                    };

                    const getMaxHeight = (node, level = 0) => {
                        if (!node.children || node.children.length === 0) {
                            return nodeHeight;
                        }
                        const maxChildHeight = Math.max(...node.children.map(child => getMaxHeight(child, level + 1)));
                        return nodeHeight + nodeGapY + maxChildHeight;
                    };

                    return {
                        width: getMaxWidth(data),
                        height: getMaxHeight(data)
                    };
                };

                const { width: treeWidth, height: treeHeight } = calculateTreeDimensions(organizationData);
                const availableWidth = containerWidth - 20;
                const availableHeight = containerHeight - 20;

                const scaleX = availableWidth / treeWidth;
                const scaleY = availableHeight / treeHeight;
                const scale = Math.min(scaleX, scaleY);

                if (isMobile) {
                    setTreeScale(Math.min(1, Math.max(0.3, scale)));
                } else {
                    setTreeScale(Math.max(0.7, Math.min(1, scale)));
                }
            }, 200);
        }
    }, [organizationData, isMobile]);

    useEffect(() => {
        if (departmentType === 'Tier2' || departmentType === 'Tier3') {
            fetchDepartments('TOTAL', 0);
        }
    }, [departmentType]);

    useEffect(() => {
        if (selectedGeneralDepartment && (departmentType === 'Tier2' || departmentType === 'Tier3')) {
            fetchDepartments('MANAGE', selectedGeneralDepartment);
        }
        if (selectedManagementDepartment && departmentType === 'Tier3') {
            fetchDepartments('TEAM', selectedManagementDepartment);
        }
    }, [selectedGeneralDepartment, selectedManagementDepartment, departmentType]);

    const fetchDepartments = async (deptType, upperDeptId = 0) => {
        try {
            const response = await axios.get(`${common.getApiUrl()}/dept`, {
                params: { upperDeptId, deptType },
                headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
            });
            if (Array.isArray(response.data)) {
                if (deptType === 'TOTAL') {
                    setGeneralDepartments(response.data);
                } else if (deptType === 'MANAGE') {
                    setManagementDepartments(response.data);
                }
            } else {
                console.error(`Expected an array but got:`, response.data);
            }
        } catch (error) {
            console.error(`Error fetching ${deptType} departments:`, error);
            alert('Failed to fetch department data.');
        }
    };

    // 조직 데이터 가져오기
    const fetchOrganizationData = async () => {
        const token = localStorage.getItem('access_token');
        const url = `${common.getApiUrl()}/dept/all`;

        try {
            const response = await axios.get(url, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            setOrganizationData(transformOrganizationData(response.data));
        } catch (error) {
            console.error('조직 데이터를 가져오는데 실패했습니다:', error);
            alert('조직 데이터를 가져올 수 없습니다.');
        }
    };

    // 조직 데이터 변환 (트리 구조로)
    const transformOrganizationData = (data) => {
        const deptMap = {};
        data.forEach(dept => {
            deptMap[dept.deptId] = { ...dept, children: [] };
            if (dept.upperDept) {
                const parent = deptMap[dept.upperDept];
                if (parent) {
                    parent.children.push(deptMap[dept.deptId]);
                }
            }
        });

        const roots = data.filter(dept => !dept.upperDept).map(dept => deptMap[dept.deptId]);
        return {
            label: '조직',
            children: roots.map(dept => transformToTreeData(dept, deptMap))
        };
    };

    const transformToTreeData = (dept, deptMap) => {
        return {
            label: (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        width: '100%',
                        height: '100%',
                        textAlign: 'center',
                        p: isMobile ? 0.1 : 1,
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        minWidth: 0,
                        '&:hover': {
                            borderColor: '#1976d2',
                            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)',
                            transform: 'translateY(-1px)',
                        }
                    }}
                    onClick={() => handleEditDepartment(dept.deptId, dept.deptNm)}
                >
                    <Typography
                        sx={{
                            fontSize: isMobile ? '8px' : '14px',
                            fontWeight: '600',
                            color: '#333',
                            lineHeight: 1,
                            wordBreak: 'break-all',
                            mb: isMobile ? 0.1 : 0.5,
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: isMobile ? 3 : 1,
                            WebkitBoxOrient: 'vertical',
                            width: '100%',
                            maxWidth: '100%',
                        }}
                    >
                        {dept.deptNm}
                    </Typography>
                    <IconButton
                        size="small"
                        sx={{
                            p: isMobile ? 0.05 : 0.25,
                            color: '#666',
                            backgroundColor: '#f8f8f8',
                            '&:hover': {
                                backgroundColor: '#e0e0e0',
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditDepartment(dept.deptId, dept.deptNm);
                        }}
                    >
                        <EditIcon sx={{ fontSize: isMobile ? '8px' : '16px' }} />
                    </IconButton>
                </Box>
            ),
            children: (dept.children || []).map(child => transformToTreeData(child, deptMap))
        };
    };

    const handleNewDepartmentChange = (event) => {
        setNewDepartment(event.target.value);
    };

    const handleGeneralDepartmentChange = (event) => {
        const generalDeptId = event.target.value;
        setSelectedGeneralDepartment(generalDeptId);
        setSelectedManagementDepartment('');
        fetchDepartments('MANAGE', generalDeptId);
    };

    const handleManagementDepartmentChange = (event) => {
        const managementDeptId = event.target.value;
        setSelectedManagementDepartment(managementDeptId);
        if (departmentType === 'Tier3') {
            fetchDepartments('TEAM', managementDeptId);
        }
    };

    const handleDepartmentTypeChange = (event) => {
        const type = event.target.value;
        setDepartmentType(type);
        setSelectedGeneralDepartment('');
        setSelectedManagementDepartment('');
        setSelectedTeamDepartment('');
        setNewDepartment('');
        setShowGeneralDepartmentSelect(type === 'Tier2' || type === 'Tier3');
        setShowManagementDepartmentSelect(type === 'Tier3');
        setShowNewDepartmentInput(type !== '');
    };

    // 새 부서 추가
    const handleAddDepartment = async () => {
        if (!newDepartment.trim()) {
            alert('부서명을 입력해주세요.');
            return;
        }

        let upperDeptId = null;
        switch (departmentType) {
            case 'Tier2':
                upperDeptId = selectedGeneralDepartment;
                break;
            case 'Tier3':
                upperDeptId = selectedManagementDepartment;
                break;
        }

        const departmentData = {
            deptNm: newDepartment,
            upperDept: upperDeptId,
            deptType: departmentType === 'Tier1' ? 'TOTAL' :
                departmentType === 'Tier2' ? 'MANAGE' :
                    departmentType === 'Tier3' ? 'TEAM' : null
        };

        try {
            const response = await axios.post(`${common.getApiUrl()}/dept`, departmentData, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 200) {
                alert('부서가 성공적으로 추가되었습니다.');
                resetForm();
                await fetchOrganizationData();
            } else {
                alert('부서를 추가하지 못했습니다. 오류를 확인해주세요.');
            }
        } catch (error) {
            console.error('Error adding department:', error);
            alert('부서를 추가하는 데 실패했습니다.');
        }
    };

    const resetForm = () => {
        setNewDepartment('');
        setDepartmentType('');
        setSelectedGeneralDepartment('');
        setSelectedManagementDepartment('');
        setShowGeneralDepartmentSelect(false);
        setShowManagementDepartmentSelect(false);
        setShowNewDepartmentInput(false);
    };

    const handleEditDepartment = (deptId, deptNm) => {
        setEditMode(true);
        setEditDepartmentId(deptId);
        setEditDepartmentName(deptNm);
    };

    // 부서 수정
    const handleUpdateDepartment = async () => {
        if (!editDepartmentName.trim()) {
            alert('부서명을 입력해주세요.');
            return;
        }

        try {
            const response = await axios.put(
                `${common.getApiUrl()}/dept/${editDepartmentId}`,
                {
                    deptId: editDepartmentId,
                    deptNm: editDepartmentName,
                },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.status === 200) {
                alert('부서가 성공적으로 수정되었습니다.');
                setEditMode(false);
                await fetchOrganizationData();
            } else {
                alert('부서를 수정하지 못했습니다. 오류를 확인해주세요.');
            }
        } catch (error) {
            console.error('Error updating department:', error);
            alert('부서를 수정하는 데 실패했습니다.');
        }
    };

    // 부서 삭제
    const handleDeleteDepartment = async () => {
        if (!window.confirm('정말로 이 부서를 삭제하시겠습니까?')) {
            return;
        }

        try {
            const response = await axios.delete(
                `${common.getApiUrl()}/dept/${editDepartmentId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    },
                }
            );

            if (response.status === 200) {
                alert('부서가 성공적으로 삭제되었습니다.');
                setEditMode(false);
                await fetchOrganizationData();
            } else {
                alert('부서를 삭제하지 못했습니다. 오류를 확인해주세요.');
            }
        } catch (error) {
            console.error('Error deleting department:', error);
            alert('부서를 삭제하는 데 실패했습니다.');
        }
    };

    const handleCancelEdit = () => {
        setEditMode(false);
        setEditDepartmentId(null);
        setEditDepartmentName('');
    };

    return (
        <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 1 : 3,
            p: isMobile ? 1 : 0, // 모바일에서 전체 패딩 추가
            boxSizing: 'border-box',
            maxWidth: '100vw',
            minWidth: 0,
            overflow: 'hidden',
        }}>
            {/* 헤더 */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: isMobile ? 0.5 : 1,
                minHeight: isMobile ? '40px' : 'auto',
                width: '100%',
                boxSizing: 'border-box',
            }}>
                <Typography
                    variant={isMobile ? "subtitle2" : "h5"}
                    sx={{
                        fontWeight: '700',
                        color: '#1a1a1a',
                        letterSpacing: '-0.02em',
                        fontSize: isMobile ? '16px' : undefined,
                    }}
                >
                    조직 관리
                </Typography>
            </Box>

            {/* 조직도 섹션 */}
            <Card
                elevation={0}
                sx={{
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderRadius: isMobile ? '8px' : '16px',
                    overflow: 'hidden',
                    flex: 1,
                    height: isMobile ? '40vh' : 'auto',
                    maxHeight: isMobile ? '40vh' : 'auto',
                    minHeight: isMobile ? '250px' : 'auto',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    // 모바일에서 mx 제거
                }}
            >
                <CardContent sx={{
                    p: isMobile ? 1 : 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    minWidth: 0,
                }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: isMobile ? 0.5 : 2,
                        flexWrap: 'nowrap',
                        gap: 0.5,
                        width: '100%',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <Typography
                                variant={isMobile ? "caption" : "subtitle1"}
                                sx={{
                                    fontWeight: '600',
                                    color: '#333',
                                    fontSize: isMobile ? '12px' : undefined,
                                }}
                            >
                                조직도
                            </Typography>
                            <Chip
                                label="클릭수정"
                                size="small"
                                variant="outlined"
                                sx={{
                                    fontSize: isMobile ? '8px' : '10px',
                                    height: isMobile ? '16px' : '24px',
                                    '& .MuiChip-label': {
                                        px: isMobile ? 0.5 : 1,
                                    }
                                }}
                            />
                        </Box>
                    </Box>

                    <Box
                        ref={orgTreeRef}
                        sx={{
                            width: '100%',
                            height: isMobile ? 'calc(100% - 40px)' : '600px',
                            minHeight: isMobile ? '200px' : '400px',
                            maxHeight: isMobile ? '300px' : '600px',
                            border: '1px solid #f0f0f0',
                            borderRadius: '4px',
                            backgroundColor: '#fafafa',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: isMobile ? 0.5 : 2,
                            flex: 1,
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={{
                                transform: `scale(${treeScale})`,
                                transformOrigin: 'center center',
                                transition: 'transform 0.3s ease-in-out',
                                width: 'fit-content',
                                height: 'fit-content',
                                maxWidth: '95%',
                                maxHeight: '95%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minWidth: 0,
                            }}
                        >
                            {organizationData && (
                                <OrgTree
                                    data={organizationData}
                                    horizontal={false}
                                    collapsable={false}
                                    expandAll={true}
                                    nodeWidth={isMobile ? 50 : 140}
                                    nodeHeight={isMobile ? 32 : 90}
                                />
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* 부서 등록/수정 폼 */}
            <Card
                elevation={0}
                sx={{
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderRadius: isMobile ? '8px' : '16px',
                    overflow: 'hidden',
                    maxHeight: isMobile ? '50vh' : 'auto',
                    mb: isMobile ? 1 : 2,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    // 모바일에서 mx 제거
                }}
            >
                <CardContent sx={{
                    p: isMobile ? 1 : 3,
                    height: '100%',
                    overflow: 'auto',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    minWidth: 0,
                }}>
                    <Typography
                        variant={isMobile ? "caption" : "subtitle1"}
                        sx={{
                            fontWeight: '600',
                            color: '#333',
                            mb: isMobile ? 1 : 2,
                            fontSize: isMobile ? '12px' : undefined,
                        }}
                    >
                        {editMode ? '부서 수정' : '부서 등록'}
                    </Typography>

                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: isMobile ? 1 : 2,
                        width: '100%',
                        minWidth: 0,
                    }}>
                        {/* 폼 필드들 */}
                        {!editMode && (
                            <>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: isMobile ? 2 : 3,
                                    width: '100%',
                                    minWidth: 0,
                                }}>
                                    <FormControl
                                        variant="outlined"
                                        size="small"
                                        sx={{ width: '100%', minWidth: 0 }}
                                    >
                                        <InputLabel sx={{ fontSize: isMobile ? '12px' : undefined }}>
                                            등록할 부서 Tier를 선택해주세요
                                        </InputLabel>
                                        <Select
                                            value={departmentType}
                                            onChange={handleDepartmentTypeChange}
                                            label="등록할 부서 Tier를 선택해주세요"
                                            sx={{
                                                borderRadius: '6px',
                                                fontSize: isMobile ? '12px' : undefined,
                                                width: '100%',
                                            }}
                                        >
                                            <MenuItem value="Tier1" sx={{ fontSize: isMobile ? '12px' : undefined }}>
                                                Tier1 (최상위 부서)
                                            </MenuItem>
                                            <MenuItem value="Tier2" sx={{ fontSize: isMobile ? '12px' : undefined }}>
                                                Tier2 (관리 부서)
                                            </MenuItem>
                                            <MenuItem value="Tier3" sx={{ fontSize: isMobile ? '12px' : undefined }}>
                                                Tier3 (팀 부서)
                                            </MenuItem>
                                        </Select>
                                    </FormControl>

                                    {showGeneralDepartmentSelect && (
                                        <FormControl
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: '100%', minWidth: 0 }}
                                        >
                                            <InputLabel sx={{ fontSize: isMobile ? '12px' : undefined }}>
                                                상위 부서 (Tier1) 선택
                                            </InputLabel>
                                            <Select
                                                value={selectedGeneralDepartment}
                                                onChange={handleGeneralDepartmentChange}
                                                label="상위 부서 (Tier1) 선택"
                                                sx={{
                                                    borderRadius: '6px',
                                                    fontSize: isMobile ? '12px' : undefined,
                                                    width: '100%',
                                                }}
                                            >
                                                {generalDepartments.map((dept) => (
                                                    <MenuItem
                                                        key={dept.deptId}
                                                        value={dept.deptId}
                                                        sx={{ fontSize: isMobile ? '12px' : undefined }}
                                                    >
                                                        {dept.deptNm}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    {showManagementDepartmentSelect && (
                                        <FormControl
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: '100%', minWidth: 0 }}
                                        >
                                            <InputLabel sx={{ fontSize: isMobile ? '12px' : undefined }}>
                                                상위 부서 (Tier2) 선택
                                            </InputLabel>
                                            <Select
                                                value={selectedManagementDepartment}
                                                onChange={handleManagementDepartmentChange}
                                                label="상위 부서 (Tier2) 선택"
                                                sx={{
                                                    borderRadius: '6px',
                                                    fontSize: isMobile ? '12px' : undefined,
                                                    width: '100%',
                                                }}
                                            >
                                                {managementDepartments.map((dept) => (
                                                    <MenuItem
                                                        key={dept.deptId}
                                                        value={dept.deptId}
                                                        sx={{ fontSize: isMobile ? '12px' : undefined }}
                                                    >
                                                        {dept.deptNm}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Box>

                                {/* 부서명 입력과 등록 버튼 */}
                                {showNewDepartmentInput && (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        width: '100%',
                                        minWidth: 0,
                                    }}>
                                        <TextField
                                            label="신규 부서명"
                                            variant="outlined"
                                            autoComplete="off"
                                            value={newDepartment || ''}
                                            onChange={handleNewDepartmentChange}
                                            size="small"
                                            InputLabelProps={{
                                                sx: { fontSize: isMobile ? '12px' : undefined }
                                            }}
                                            inputProps={{
                                                sx: { fontSize: isMobile ? '12px' : undefined }
                                            }}
                                            sx={{
                                                width: '100%',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleAddDepartment}
                                            disabled={!newDepartment.trim()}
                                            size="small"
                                            startIcon={<AddIcon sx={{ fontSize: isMobile ? '14px' : undefined }} />}
                                            sx={{
                                                borderRadius: '6px',
                                                fontWeight: '600',
                                                py: isMobile ? 1 : 1.5,
                                                fontSize: isMobile ? '12px' : undefined,
                                                minHeight: isMobile ? '36px' : '40px',
                                                width: '100%',
                                            }}
                                        >
                                            부서 등록
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}

                        {editMode && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                width: '100%',
                                minWidth: 0,
                            }}>
                                <TextField
                                    label="부서명 수정"
                                    variant="outlined"
                                    autoComplete="off"
                                    value={editDepartmentName || ''}
                                    onChange={(e) => setEditDepartmentName(e.target.value)}
                                    size="small"
                                    InputLabelProps={{
                                        sx: { fontSize: isMobile ? '12px' : undefined }
                                    }}
                                    inputProps={{
                                        sx: { fontSize: isMobile ? '12px' : undefined }
                                    }}
                                    sx={{
                                        width: '100%',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                        }
                                    }}
                                />
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    gap: isMobile ? 0.5 : 1,
                                    width: '100%',
                                }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleUpdateDepartment}
                                        disabled={!editDepartmentName.trim()}
                                        size="small"
                                        startIcon={<SaveIcon sx={{ fontSize: isMobile ? '14px' : undefined }} />}
                                        sx={{
                                            borderRadius: '6px',
                                            fontWeight: '600',
                                            py: isMobile ? 1 : 1.5,
                                            fontSize: isMobile ? '12px' : undefined,
                                            minHeight: isMobile ? '36px' : '40px',
                                            flex: 1,
                                        }}
                                    >
                                        저장
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleDeleteDepartment}
                                        size="small"
                                        startIcon={<DeleteIcon sx={{ fontSize: isMobile ? '14px' : undefined }} />}
                                        sx={{
                                            borderRadius: '6px',
                                            fontWeight: '600',
                                            py: isMobile ? 1 : 1.5,
                                            fontSize: isMobile ? '12px' : undefined,
                                            minHeight: isMobile ? '36px' : '40px',
                                            flex: 1,
                                        }}
                                    >
                                        삭제
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        onClick={handleCancelEdit}
                                        size="small"
                                        startIcon={<CancelIcon sx={{ fontSize: isMobile ? '14px' : undefined }} />}
                                        sx={{
                                            borderRadius: '6px',
                                            fontWeight: '600',
                                            py: isMobile ? 1 : 1.5,
                                            fontSize: isMobile ? '12px' : undefined,
                                            minHeight: isMobile ? '36px' : '40px',
                                            flex: 1,
                                        }}
                                    >
                                        취소
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default validationAuth(OrgManagement);